import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import { FC, useEffect, useState, MouseEvent, useMemo, useRef, useLayoutEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import Joyride, { ACTIONS, CallBackProps, EVENTS, LIFECYCLE, STATUS, Step } from 'react-joyride';
import { logoutRequest } from 'redux/reducers/auth/reducer';
import { DEFAULT_AUTHORIZED_PATH, filterNavGrouped, isNavGroup, TNavEntry } from 'routes/constants';
import { MenuList } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Tour } from '@mui/icons-material';
import { EasTooltip } from '../../ui/EasTooltip';
import { getUserInfo } from 'redux/reducers/user/selectors';
import { selectRealmRoles } from 'redux/reducers/auth/selectors';
import { getUserInfoRequest, getUsersListRequest } from 'redux/reducers/user/reducer';
import { getUserImage } from 'utils/getUserImage';
import { stringAvatar } from 'utils/stringAvatar';
import useInterval from 'use-interval';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { COLORS, EThemeName, getTheme, setTheme } from 'services/constants/colors';
import { UserMenu } from './UserMenu';
import { MessagesMenu } from './MessagesMenu';
import { NotificationsIndicator } from './NotificationsIndicator';
import { LastCommentsIndicator } from './LastCommentsIndicator';
import { PageSearch } from './PageSearch';
import { getEMerchantRequest } from '../../redux/reducers/monthlyFee/reducer';
import { NavGroupButton } from './NavGroupButton';
import { renderNavMenuEntries, toNavMenuItems } from './NavMenuEntries';
import { isPathActive, slugifyName } from './NavGroupButton/utils';
import {
	getNavTourSeenStorageKey,
	NAV_GROUP_TOUR_DESCRIPTIONS,
	NAV_IDS,
	NAV_ITEM_TOUR_DESCRIPTIONS,
	NAV_TOUR_STYLES,
} from './constants';
import { getCountryListRequest } from '../../redux/reducers/country/reducer';
import { IS_NEMAN } from '../../services/constants/env';

const getEntryKey = (entry: TNavEntry): string =>
	isNavGroup(entry) ? `group:${entry.name}` : `item:${entry.path}`;

const getItemDomId = (itemName: string): string =>
	`${NAV_IDS.ITEM_PREFIX}-${slugifyName(itemName)}`;

const getGroupDomId = (groupName: string): string =>
	`${NAV_IDS.GROUP_PREFIX}-${slugifyName(groupName)}`;

const getGroupItemDomId = (groupName: string, itemName: string): string =>
	`${NAV_IDS.GROUP_ITEM_PREFIX}-${slugifyName(groupName)}-${slugifyName(itemName)}`;

// Custom data attached to each tour step. `groupKey` is the group name whose
// dropdown must be open while the step is active.
type TNavTourStepData = { groupKey?: string };

const ResponsiveAppBar: FC = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();

	const userinfo = useSelector(getUserInfo);
	const roles = userinfo?.roles;
	const realmRoles = useSelector(selectRealmRoles);

	const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
	const handleOpenNavMenu = (event: MouseEvent<HTMLElement>) => {
		setAnchorElNav(event.currentTarget);
	};
	const handleCloseNavMenu = () => {
		setAnchorElNav(null);
	};

	const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
	const handleOpenUserMenu = (event: MouseEvent<HTMLElement>) => {
		setAnchorElUser(event.currentTarget);
	};
	const handleCloseUserMenu = () => {
		setAnchorElUser(null);
	};

	const [anchorElMessages, setAnchorElMessages] = useState<null | HTMLElement>(null);
	const handleCloseMessagesMenu = () => {
		setAnchorElMessages(null);
	};

	// Desktop nav overflow handling: each top-level entry (item or group) is
	// treated as a single unit that either fits in the bar or is moved to the
	// "More" menu in its entirety.
	const desktopNavRef = useRef<HTMLDivElement | null>(null);
	const measureRef = useRef<HTMLDivElement | null>(null);
	const [visibleNavEntries, setVisibleNavEntries] = useState<TNavEntry[]>([]);
	const [overflowNavEntries, setOverflowNavEntries] = useState<TNavEntry[]>([]);
	const [anchorElMore, setAnchorElMore] = useState<null | HTMLElement>(null);
	const handleOpenMoreMenu = (event: MouseEvent<HTMLElement>) => {
		setAnchorElMore(event.currentTarget);
	};
	const handleCloseMoreMenu = () => {
		setAnchorElMore(null);
	};

	// Tour state. `forcedOpenGroupKey` corresponds to the group whose dropdown
	// is forced open while the tour is highlighting items inside it.
	const [tourRun, setTourRun] = useState(false);
	const [forcedOpenGroupKey, setForcedOpenGroupKey] = useState<string | null>(null);

	const allNavEntries = useMemo<TNavEntry[]>(() => {
		if ((roles && roles.length > 0) || realmRoles.length > 0) {
			return filterNavGrouped(roles ?? [], realmRoles);
		}
		return [];
	}, [roles, realmRoles]);

	useEffect(() => {
		if (IS_NEMAN) {
			const currentTheme = getTheme();
			if (currentTheme !== EThemeName.NEMAN_LIGHT && currentTheme !== EThemeName.NEMAN_DARK) {
				setTheme(EThemeName.NEMAN_LIGHT);
			}
		}
	}, []);

	useLayoutEffect(() => {
		const recalc = () => {
			const container = desktopNavRef.current;
			const measure = measureRef.current;
			if (!container || !measure) return;

			const availableWidth = container.clientWidth;
			if (availableWidth === 0) {
				setVisibleNavEntries(allNavEntries);
				setOverflowNavEntries([]);
				return;
			}

			const children = Array.from(measure.children) as HTMLElement[];
			if (children.length === 0) {
				setVisibleNavEntries(allNavEntries);
				setOverflowNavEntries([]);
				return;
			}

			const itemWidths = children.map((el) => el.offsetWidth);
			const totalWidth = itemWidths.reduce((acc, w) => acc + w, 0);
			if (totalWidth <= availableWidth) {
				setVisibleNavEntries(allNavEntries);
				setOverflowNavEntries([]);
				return;
			}

			const moreButtonWidth = 56;
			const capacity = Math.max(availableWidth - moreButtonWidth, 0);
			let accWidth = 0;
			let cutIndex = allNavEntries.length;
			for (let i = 0; i < itemWidths.length; i += 1) {
				const next = accWidth + itemWidths[i];
				if (next <= capacity) {
					accWidth = next;
				} else {
					cutIndex = i;
					break;
				}
			}

			setVisibleNavEntries(allNavEntries.slice(0, cutIndex));
			setOverflowNavEntries(allNavEntries.slice(cutIndex));
		};

		recalc();
		const ro = new ResizeObserver(() => recalc());
		if (desktopNavRef.current) ro.observe(desktopNavRef.current);
		return () => {
			ro.disconnect();
		};
	}, [allNavEntries]);

	useEffect(() => {
		dispatch(getUserInfoRequest());
		dispatch(getUsersListRequest());
		dispatch(getCountryListRequest());
		// EM names for the comments/notifications indicators.
		dispatch(getEMerchantRequest());
	}, [dispatch]);

	const handleLogout = () => {
		setAnchorElMessages(null);
		dispatch(logoutRequest());
	};

	useInterval(() => {
		dispatch(getUserInfoRequest());
	}, 20_000);

	const navigateAndClose = (path: string, close?: () => void) => {
		close?.();
		navigate(path);
	};

	// Build the tour walkthrough from the currently visible nav entries.
	// Each group expands into one step for its trigger plus one step per row
	// in its dropdown (nested groups are collapsed into a single row); group
	// steps carry `data.groupKey` so the dropdown stays open while the tour is
	// positioned over them.
	const tourSteps = useMemo<Step[]>(() => {
		if (allNavEntries.length === 0) return [];

		const steps: Step[] = [];

		if (!IS_NEMAN) {
			steps.push({
				target: `#${NAV_IDS.LOGO}`,
				content: 'Click the EAS logo to return to the default page (EM Management).',
				disableBeacon: true,
			});
		}

		visibleNavEntries.forEach((entry) => {
			if (isNavGroup(entry)) {
				const groupId = getGroupDomId(entry.name);
				const groupContent = NAV_GROUP_TOUR_DESCRIPTIONS[entry.name] ?? entry.name;
				const groupData: TNavTourStepData = { groupKey: entry.name };

				steps.push({
					target: `#${groupId}`,
					content: groupContent,
					data: groupData,
					disableBeacon: true,
				});

				toNavMenuItems(entry.items).forEach((item) => {
					steps.push({
						target: `#${getGroupItemDomId(entry.name, item.name)}`,
						content: NAV_ITEM_TOUR_DESCRIPTIONS[item.name] ?? item.name,
						data: groupData,
						disableBeacon: true,
					});
				});
			} else {
				steps.push({
					target: `#${getItemDomId(entry.name)}`,
					content: NAV_ITEM_TOUR_DESCRIPTIONS[entry.name] ?? entry.name,
					disableBeacon: true,
				});
			}
		});

		if (overflowNavEntries.length > 0) {
			steps.push({
				target: `#${NAV_IDS.MORE_BUTTON}`,
				content: "Items that don't fit in the menu are accessible from here.",
				disableBeacon: true,
			});
		}

		steps.push({
			target: `#${NAV_IDS.USER_AVATAR}`,
			content: 'Open your user menu to manage settings, theme and to sign out.',
			disableBeacon: true,
		});

		steps.push({
			target: `#${NAV_IDS.TOUR_BUTTON}`,
			content: 'You can replay this tour any time by clicking here.',
			disableBeacon: true,
		});

		return steps;
	}, [allNavEntries.length, visibleNavEntries, overflowNavEntries]);

	const userId = userinfo?.id;

	const markTourSeen = () => {
		if (!userId) return;
		try {
			localStorage.setItem(getNavTourSeenStorageKey(userId), '1');
		} catch {
			// Storage may be unavailable (e.g. private mode). Failing to persist
			// is acceptable here — the worst case is the tour shows again next
			// time.
		}
	};

	const handleStartTour = () => {
		const firstStepGroup = (tourSteps[0]?.data as TNavTourStepData | undefined)?.groupKey ?? null;
		setForcedOpenGroupKey(firstStepGroup);
		setTourRun(true);
	};

	// Auto-start the tour the first time a user lands on the page (per browser).
	// We mark it as seen at start so a mid-tour reload doesn't re-trigger it.
	useEffect(() => {
		if (!userId) return;
		if (tourRun) return;
		if (tourSteps.length === 0) return;
		if (visibleNavEntries.length === 0) return;

		let alreadySeen = false;
		try {
			alreadySeen = !!localStorage.getItem(getNavTourSeenStorageKey(userId));
		} catch {
			// If localStorage isn't available, skip auto-start to avoid being
			// repeatedly intrusive.
			alreadySeen = true;
		}
		if (alreadySeen) return;

		markTourSeen();
		// Delay slightly so the layout has settled and Joyride finds targets.
		const timer = setTimeout(() => {
			handleStartTour();
		}, 600);
		return () => clearTimeout(timer);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userId, tourSteps.length, visibleNavEntries.length]);

	const handleTourCallback = (data: CallBackProps) => {
		const { action, status, lifecycle, step, type } = data;

		if (status === STATUS.FINISHED || status === STATUS.SKIPPED || action === ACTIONS.CLOSE) {
			setTourRun(false);
			setForcedOpenGroupKey(null);
			markTourSeen();
			return;
		}

		// Sync the open group with the current step. Joyride emits LIFECYCLE.READY
		// just before showing the tooltip; we update at that moment so the
		// dropdown is mounted in the DOM by the time the spotlight is rendered.
		if (lifecycle === LIFECYCLE.READY || lifecycle === LIFECYCLE.TOOLTIP) {
			const stepData = (step?.data as TNavTourStepData | undefined) ?? undefined;
			setForcedOpenGroupKey(stepData?.groupKey ?? null);
		}

		// If a target can't be found, just clear the forced group so the user
		// isn't left with a dropdown stuck open.
		if (type === EVENTS.TARGET_NOT_FOUND) {
			setForcedOpenGroupKey(null);
		}
	};

	return (
		<AppBar
			position="static"
			sx={{
				marginBottom: 1,
				backgroundColor: COLORS.PAPER,
				color: COLORS.PRIMARY,
				borderRadius: '0!important',
				backgroundImage: 'none',
			}}
		>
			<Joyride
				steps={tourSteps}
				run={tourRun}
				continuous
				showSkipButton
				showProgress
				spotlightPadding={6}
				styles={NAV_TOUR_STYLES}
				callback={handleTourCallback}
			/>
			<div style={{ margin: '0 15px' }}>
				<Toolbar
					disableGutters
					sx={{
						'&.MuiToolbar-root': {
							minHeight: '54px!important',
						},
					}}
				>
					<Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
						{IS_NEMAN ? (
							<Box sx={{ display: { xs: 'none', md: 'flex' }, mr: 1 }}>
								<Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 0.5 }}>
									<Typography
										sx={{
											fontSize: '1.2rem',
											fontWeight: 900,
											color: '#0f172a',
											cursor: 'pointer',
										}}
										onClick={() => navigate('/')}
									>
										Neman
									</Typography>
									<Typography
										component="span"
										sx={{ color: '#3399ff', fontSize: '1.2rem', fontWeight: 900 }}
									>
										IOSS
									</Typography>
								</Box>
							</Box>
						) : (
							<Avatar
								id={NAV_IDS.LOGO}
								alt="EAS Dashboard"
								src={COLORS.LOGO}
								sx={{ display: { xs: 'none', md: 'flex' }, mr: 1, cursor: 'pointer' }}
								onClick={() => navigate(DEFAULT_AUTHORIZED_PATH)}
							/>
						)}
					</Box>
					<Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
						<IconButton
							size="large"
							aria-label="account of current user"
							aria-controls="menu-appbar"
							aria-haspopup="true"
							onClick={handleOpenNavMenu}
							color="inherit"
						>
							<MenuIcon />
						</IconButton>
						<Menu
							id="menu-appbar"
							anchorEl={anchorElNav}
							anchorOrigin={{
								vertical: 'bottom',
								horizontal: 'left',
							}}
							keepMounted
							transformOrigin={{
								vertical: 'top',
								horizontal: 'left',
							}}
							open={Boolean(anchorElNav)}
							onClose={handleCloseNavMenu}
							sx={{
								display: { xs: 'block', md: 'none' },
							}}
						>
							<MenuList>
								{renderNavMenuEntries({
									entries: allNavEntries,
									locationPathname: location.pathname,
									onItemClick: (path) => navigateAndClose(path, handleCloseNavMenu),
									keyPrefix: 'mobile-',
								})}
							</MenuList>
						</Menu>
					</Box>
					<Box
						ref={desktopNavRef}
						sx={{
							flexGrow: 1,
							display: { xs: 'none', md: 'flex' },
							alignItems: 'center',
							gap: 0,
							overflow: 'hidden',
						}}
					>
						{visibleNavEntries.map((entry) => {
							if (isNavGroup(entry)) {
								return (
									<NavGroupButton
										key={getEntryKey(entry)}
										name={entry.name}
										items={entry.items}
										locationPathname={location.pathname}
										onItemClick={(path) => navigate(path)}
										forceOpen={forcedOpenGroupKey === entry.name}
										groupId={getGroupDomId(entry.name)}
										getItemId={(item) => getGroupItemDomId(entry.name, item.name)}
									/>
								);
							}
							return (
								<Button
									key={getEntryKey(entry)}
									id={getItemDomId(entry.name)}
									onClick={() => navigate(entry.path)}
									sx={{
										color: isPathActive(location.pathname, entry.path) ? '#5aa1bf' : 'primary',
										textTransform: 'none',
										fontWeight: 550,
										fontSize: '0.8rem',
										justifyContent: 'flex-start',
										lineHeight: '1.2rem',
										whiteSpace: 'nowrap',
									}}
								>
									{entry.name}
								</Button>
							);
						})}
						{overflowNavEntries.length > 0 && (
							<>
								<IconButton
									id={NAV_IDS.MORE_BUTTON}
									onClick={handleOpenMoreMenu}
									color="inherit"
									sx={{ p: 0, m: 0, ml: 1 }}
								>
									<MoreHorizIcon />
								</IconButton>
								<Menu
									anchorEl={anchorElMore}
									open={Boolean(anchorElMore)}
									onClose={handleCloseMoreMenu}
									anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
									transformOrigin={{ vertical: 'top', horizontal: 'right' }}
								>
									{renderNavMenuEntries({
										entries: overflowNavEntries,
										locationPathname: location.pathname,
										onItemClick: (path) => navigateAndClose(path, handleCloseMoreMenu),
										keyPrefix: 'more-',
									})}
								</Menu>
							</>
						)}
					</Box>

					{/* Hidden measurement container to calculate widths of top-level entries */}
					<Box
						ref={measureRef}
						sx={{
							position: 'fixed',
							left: '-10000px',
							top: 0,
							visibility: 'hidden',
							pointerEvents: 'none',
							display: { xs: 'none', md: 'flex' },
							alignItems: 'center',
							gap: 0,
						}}
					>
						{allNavEntries.map((entry) => (
							<Button
								key={`measure-${getEntryKey(entry)}`}
								endIcon={
									isNavGroup(entry) ? <KeyboardArrowDownIcon sx={{ ml: -0.5 }} /> : undefined
								}
								sx={{
									textTransform: 'none',
									fontWeight: 550,
									fontSize: '0.8rem',
									justifyContent: 'flex-start',
									lineHeight: '1.2rem',
									whiteSpace: 'nowrap',
								}}
							>
								{entry.name}
							</Button>
						))}
					</Box>

					<Typography
						variant="h5"
						noWrap
						component="a"
						href="/"
						sx={{
							mr: 2,
							display: { xs: 'flex', md: 'none' },
							flexGrow: 1,
							fontFamily: 'monospace',
							fontWeight: 700,
							letterSpacing: '.3rem',
							color: 'inherit',
							textDecoration: 'none',
						}}
					>
						Control Center
					</Typography>
					<Box sx={{ flexGrow: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
						<PageSearch />
						<LastCommentsIndicator />
						<NotificationsIndicator />
						<EasTooltip title="Take a tour of the navigation">
							{/* The span lets the tooltip work while the button is disabled */}
							<span>
								<IconButton
									id={NAV_IDS.TOUR_BUTTON}
									color="primary"
									onClick={handleStartTour}
									sx={{ p: 0.5 }}
									disabled={tourSteps.length === 0}
								>
									<Tour sx={{ fontSize: '1.6rem' }} />
								</IconButton>
							</span>
						</EasTooltip>
						<EasTooltip title="Open settings">
							<IconButton id={NAV_IDS.USER_AVATAR} onClick={handleOpenUserMenu} sx={{ p: 0 }}>
								<Avatar
									src={getUserImage(userinfo?.email || '')}
									{...stringAvatar(userinfo?.fullName || '')}
								/>
							</IconButton>
						</EasTooltip>
						<UserMenu
							anchorElUser={anchorElUser}
							handleCloseUserMenu={handleCloseUserMenu}
							handleLogout={handleLogout}
						/>
						<MessagesMenu anchorEl={anchorElMessages} handleCloseMenu={handleCloseMessagesMenu} />
					</Box>
				</Toolbar>
			</div>
		</AppBar>
	);
};
export default ResponsiveAppBar;
